import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './contexts/ToastContext';
import { ActionsCountProvider } from './contexts/ActionsCountContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleGuard from './components/RoleGuard';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import RoosterPage from './pages/coach/RoosterPage';
import LesAanmakenPage from './pages/coach/LesAanmakenPage';
import LesBewerkenPage from './pages/coach/LesBewerkenPage';
import ClientsPage from './pages/coach/ClientsPage';
import ProspectsPage from './pages/coach/ProspectsPage';
import ClientDetailPage from './pages/coach/ClientDetailPage';
import ClientTemplateFormPage from './pages/coach/ClientTemplateFormPage';
import FacturenPage from './pages/coach/FacturenPage';
import OpzeggingenPage from './pages/coach/OpzeggingenPage';
import ReceptenPage from './pages/coach/ReceptenPage';
import RecipeFormPage from './pages/coach/RecipeFormPage';
import OefeningenPage from './pages/coach/OefeningenPage';
import InstellingenPage from './pages/admin/InstellingenPage';
import AbonnementenPage from './pages/admin/AbonnementenPage';
import FactuurtekstenPage from './pages/admin/FactuurtekstenPage';
import EmailtekstenPage from './pages/admin/EmailtekstenPage';
import CronDashboardPage from './pages/admin/CronDashboardPage';
import GebruikersBeheerPage from './pages/admin/GebruikersBeheerPage';
import BugrapportenPage from './pages/admin/BugrapportenPage';
import ClientRoosterPage from './pages/client/RoosterPage';
import ClientFacturenPage from './pages/client/FacturenPage';
import ClientCreditsPage from './pages/client/ClientCreditsPage';
import ClientLessenPage from './pages/client/ClientLessenPage';
import ClientInstellingenPage from './pages/client/ClientInstellingenPage';
import BuddyPage from './pages/client/BuddyPage';
import ClientMijnAbonnementPage from './pages/client/MijnAbonnementPage';
import ClientAfsprakenPage from './pages/client/AfsprakenPage';
import ClientVoedingPage from './pages/client/VoedingPage';
import ClientWeekschemaPage from './pages/client/WeekschemaPage';
import ClientRecipeDetailPage from './pages/client/RecipeDetailPage';
import ClientTrainingslogboekPage from './pages/client/TrainingslogboekPage';
import BroadcastPushPage from './pages/coach/BroadcastPushPage';
import ActiesPage from './pages/coach/ActiesPage';
import NietGelogdeSessiesPage from './pages/coach/NietGelogdeSessiesPage';
import CoachAanvragenPage from './pages/coach/CoachAanvragenPage';
import OvereenkomstPage from './pages/OvereenkomstPage';
import BetalingBevestigingPage from './pages/BetalingBevestigingPage';
import AccountVerwijderenPage from './pages/AccountVerwijderenPage';
import ResetWachtwoordPage from './pages/ResetWachtwoordPage';
import HomeRedirect from './components/HomeRedirect';
import DashboardPage from './pages/coach/DashboardPage';

const COACH_ROLES  = ['superadmin', 'admin_coach', 'coach'];
const ADMIN_ROLES  = ['superadmin', 'admin_coach'];
const CLIENT_ROLES = ['client'];

export default function App() {
  return (
    <ActionsCountProvider>
    <ToastProvider>
      <BrowserRouter>
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/geen-toegang" element={<UnauthorizedPage />} />
        <Route path="/overeenkomst/:token" element={<OvereenkomstPage />} />
        <Route path="/betaling-bevestiging/:invoiceId" element={<BetalingBevestigingPage />} />
        {/* Publiek, zonder auth-guard — harde Google Play-eis voor de
            "Account verwijderen"-link in de Play Console. */}
        <Route path="/account-verwijderen" element={<AccountVerwijderenPage />} />
        {/* Publiek, zonder auth-guard — vangt de Supabase wachtwoord-reset-
            recovery-link op (klik in e-mail, geen ingelogde sessie). */}
        <Route path="/reset-wachtwoord" element={<ResetWachtwoordPage />} />
        <Route path="/account-activeren" element={<ResetWachtwoordPage activationMode />} />

        <Route element={<ProtectedRoute />}>
          {/* Coach routes */}
          <Route element={<RoleGuard allowed={COACH_ROLES} />}>
            <Route element={<Layout />}>
              <Route path="/coach/dashboard" element={<DashboardPage />} />
              <Route path="/coach/rooster" element={<RoosterPage />} />
              <Route path="/coach/les/nieuw" element={<LesAanmakenPage />} />
              <Route path="/coach/les/:lessonId/bewerken" element={<LesBewerkenPage />} />
              <Route path="/coach/clients" element={<ClientsPage />} />
              <Route path="/coach/prospects" element={<ProspectsPage />} />
              <Route path="/coach/clients/:id" element={<ClientDetailPage />} />
              <Route path="/coach/clients/:id/sjabloon/nieuw" element={<ClientTemplateFormPage />} />
              <Route path="/coach/clients/:id/sjabloon/:templateId" element={<ClientTemplateFormPage />} />
              <Route path="/coach/facturen" element={<FacturenPage />} />
              <Route path="/coach/opzeggingen" element={<OpzeggingenPage />} />
              <Route path="/coach/recepten" element={<ReceptenPage />} />
              <Route path="/coach/recepten/nieuw" element={<RecipeFormPage />} />
              <Route path="/coach/recepten/:id/bewerken" element={<RecipeFormPage />} />
              <Route path="/coach/oefeningen" element={<OefeningenPage />} />
              <Route path="/coach/push" element={<BroadcastPushPage />} />
              <Route path="/coach/aanvragen" element={<CoachAanvragenPage />} />
              <Route path="/coach/acties" element={<ActiesPage />} />
              <Route path="/coach/sessies-niet-gelogd" element={<NietGelogdeSessiesPage />} />
              <Route path="/coach/overeenkomst/opstellen/:clientId" element={<OvereenkomstPage />} />
              <Route path="/mijn-profiel" element={<InstellingenPage view="profile" />} />
              <Route path="/instellingen" element={<Navigate to="/mijn-profiel" replace />} />
            </Route>
          </Route>

          {/* Admin routes */}
          <Route element={<RoleGuard allowed={ADMIN_ROLES} />}>
            <Route element={<Layout />}>
              <Route path="/beheer/abonnementen" element={<AbonnementenPage />} />
              <Route path="/beheer/locaties" element={<InstellingenPage view="locations" />} />
              <Route path="/beheer/organisatie/:section?" element={<InstellingenPage view="organization" />} />
              <Route path="/beheer/communicatie" element={<Navigate to="/beheer/communicatie/email" replace />} />
              <Route path="/beheer/communicatie/facturen" element={<FactuurtekstenPage />} />
              <Route path="/beheer/communicatie/email" element={<EmailtekstenPage />} />
              <Route path="/instellingen/abonnementen" element={<Navigate to="/beheer/abonnementen" replace />} />
              <Route path="/instellingen/factuurteksten" element={<Navigate to="/beheer/communicatie/facturen" replace />} />
              <Route path="/instellingen/emailteksten" element={<Navigate to="/beheer/communicatie/email" replace />} />
              <Route path="/admin/gebruikers" element={<GebruikersBeheerPage />} />
              <Route path="/admin/cron-dashboard" element={<CronDashboardPage />} />
              <Route path="/admin/bugrapporten" element={<BugrapportenPage />} />
            </Route>
          </Route>

          {/* Client routes */}
          <Route element={<RoleGuard allowed={CLIENT_ROLES} />}>
            <Route element={<Layout />}>
              <Route path="/client/rooster" element={<ClientRoosterPage />} />
              <Route path="/client/voeding" element={<ClientVoedingPage />} />
              <Route path="/client/voeding/weekschema" element={<ClientWeekschemaPage />} />
              <Route path="/client/voeding/recept/:id" element={<ClientRecipeDetailPage />} />
              <Route path="/client/facturen" element={<ClientFacturenPage />} />
              <Route path="/client/credits" element={<ClientCreditsPage />} />
              <Route path="/client/lessen" element={<ClientLessenPage />} />
              <Route path="/client/afspraken" element={<ClientAfsprakenPage />} />
              <Route path="/client/trainingslogboek" element={<ClientTrainingslogboekPage />} />
              <Route path="/mijn-abonnement" element={<ClientMijnAbonnementPage />} />
              <Route path="/client/buddies" element={<BuddyPage />} />
              <Route path="/client/instellingen" element={<ClientInstellingenPage />} />
            </Route>
          </Route>

          <Route path="/" element={<HomeRedirect />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </ToastProvider>
    </ActionsCountProvider>
  );
}
